import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTheme,
  type Theme,
} from "@mui/material";
import {
  Check,
  ChevronDown,
  ChevronUp,
  CircleAlert,
  Download,
  FileText,
  History as HistoryIcon,
  Image as ImageIcon,
  Paperclip,
  RefreshCw,
  Send,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  Upload,
} from "lucide-react";
import { useRef, useState, type ReactNode } from "react";
import {
  DocumentRequestItem,
  DocumentUpload,
  FieldNote,
  FileDecision,
  FormField,
  PaymentProofDetails,
  ResolvedDocument,
  StepUpload,
} from "../dtos";
import { useStepMutations } from "../hooks/use-step-mutations.hook";
import ActionConfirmModal, { ActionConfirmRequest } from "./action-confirm-modal";
import InlineAppeal from "./appeal-thread-inline";
import DocRequestDialog from "./doc-request-dialog";
import DocumentExtraction from "./document-extraction";
import { fieldInputSx, primaryButtonSx, secondaryButtonSx, SelectChevron } from "./field-shell";
import TwoSidedDocument from "./two-sided-document";
import { displayFileName } from "../utils/file-name";
import { toast } from "../utils/toast";
import {NoteAttachmentList} from "./note-attachments";
import {StepHistoryPanel} from "./context-widgets";

const inlineHistoryEnabled = (field: FormField, stepUid: string | undefined): boolean =>
  field.showHistory === true && Boolean(stepUid);

const InlineDocumentHistory = ({field, fieldKey, stepUid, enabled}: {
  field: FormField;
  fieldKey: string;
  stepUid?: string;
  enabled: boolean;
}) => {
  if (!enabled) return null;
  const sourceStep = /^@s(\d+):/.exec(fieldKey);
  const localScope = field.ref ? "pool" : "step";
  return (
    <StepHistoryPanel
      key={JSON.stringify([stepUid, fieldKey])}
      stepUid={stepUid}
      inline
      query={{
        fieldKey,
        scope: sourceStep ? "upstream" : localScope,
        ...(sourceStep ? {sourceStepOrder: Number(sourceStep[1])} : {}),
      }}
    />
  );
};

const decisionLabel = (decision: FileDecision): string => {
  switch (decision) {
    case "ACCEPTED":
      return "Accepted";
    case "REJECTED":
      return "Rejected";
    // §Conditional (live bite, 2026-08-05): approved WITH conditions — the reviewer's note rides
    // rejection_reason; rendering it as plain "Uploaded" hid the verdict entirely in the CRM
    case "CONDITIONAL":
      return "Conditional";
    default:
      return "Uploaded";
  }
};

/** §Manual-AML: the file's screening outcome as a chip tone (matches WM's card). */
const amlTone = (aml: DocumentUpload["aml"]): { label: string; color: string; bg: string } => {
  const status = (aml?.status ?? "").toUpperCase();
  if (aml?.stale) return { label: "AML re-check needed", color: "#92400e", bg: "#fef3c7" };
  if (status === "CLEAR" || status === "NO_MATCH")
    return { label: "AML clear", color: "#166534", bg: "#dcfce7" };
  if (/MATCH|HIT/.test(status))
    return { label: "AML match", color: "#b91c1c", bg: "#fee2e2" };
  return { label: `AML ${status.toLowerCase()}`, color: "#6b7280", bg: "#f3f4f6" };
};

/**
 * A document requirement — WM-parity card: uploads (typed, multi-file, replace, superseded /
 * resubmitted markers), the extracted-fields block (OCR review: values + per-value chips + inline
 * edit + confirm), per-file AML screening, and the red "Below documents required" banner when a
 * file was rejected in credit review.
 */
/** Renders `node` only when `condition` holds — keeps boolean operators out of the card's JSX. */
const renderWhen = (condition: boolean, node: ReactNode): ReactNode => (condition ? node : null);

/**
 * §Late-docs: WM derives per-control capabilities for a late-addable document — an APPROVED Credit Documents
 * step still takes uploads (a single-file document versions, a multi-file one accumulates) while replace and
 * delete close from the step's first settlement. These axes may override a settled EDIT placement because
 * `agentEditable` is false there by design. An authored VIEW placement remains read-only even if the generic
 * resolved document capabilities are permissive.
 */
const lateDocsControls = (
  document: ResolvedDocument | null | undefined,
  isGlobalDocument: boolean,
  disabled: boolean,
  viewOnly: boolean
): { uploadDisabled: boolean; replaceDisabled: boolean; deleteDisabled: boolean; appendOnly: boolean } => {
  if (viewOnly) {
    return {uploadDisabled: true, replaceDisabled: true, deleteDisabled: true, appendOnly: false};
  }
  const capabilities = isGlobalDocument ? document?.capabilities : undefined;
  return {
    uploadDisabled: capabilities ? !capabilities.upload : disabled,
    replaceDisabled: capabilities ? !capabilities.replace : disabled,
    deleteDisabled: capabilities ? !capabilities.delete : disabled,
    appendOnly: Boolean(document?.lateAdditions) && Boolean(capabilities) && !capabilities?.delete,
  };
};

/** A live file offers Replace while replace is open OR the file was returned (rejected / conditional) —
 * unless the review that returned it has not submitted yet (§Under-review: WM refuses that swap). */
const replaceControlShown = (input: {
  isGlobalDocument: boolean;
  replaceDisabled: boolean;
  viewOnly: boolean;
  needsChange: boolean;
  conditional: boolean;
  underReview: boolean;
  canCorrectReplacement?: boolean;
  superseded: boolean;
}): boolean =>
  input.isGlobalDocument &&
  !input.viewOnly &&
  (!input.replaceDisabled ||
    ((input.needsChange || input.conditional) && !input.underReview) ||
    input.canCorrectReplacement === true) &&
  !input.superseded;

/** §Late-docs-note: one note per addition — it rides the FIRST picked file; the row anchors to the document, not a file. */
const noteOnFirstFile = (comment: string | undefined, index: number): { comment?: string } =>
  comment && index === 0 ? { comment } : {};

/**
 * §Late-docs-note: the confirm a settled-step pick goes through — names the file(s), says what adding does
 * (reopens / extends the credit review) and offers an optional note to credit. `null` keeps the modal closed.
 */
const lateAdditionRequest = (
  files: File[] | null,
  label: string | undefined
): ActionConfirmRequest | null => {
  if (!files) return null;
  const names = files.map((file) => displayFileName(file.name)).join(", ");
  return {
    // an authored EMPTY label must still fall back — `??` alone would render "Add to "
    title: `Add to ${(label ?? "").trim() || "document"}`,
    message: `${names} — adding a file reopens or extends the credit review; existing files stay as history.`,
    commentMode: "optional",
    commentLabel: "Note for credit (optional)",
    confirmLabel: files.length > 1 ? "Add files" : "Add file",
  };
};

/**
 * §Late-docs-note: the notes sales attached to late additions of this document — read-only rows; the reviewer
 * reads the same rows beside the file in WM. Renders nothing when there are none.
 */
const DocumentNotes = ({ notes }: { notes?: FieldNote[] }) => {
  const colors = useTheme().nawy.colors;
  if (!notes?.length) return null;
  return (
    <Stack spacing={0.5} sx={{ mt: 1 }}>
      {notes.map((note) => (
        <Box
          key={note.uid}
          sx={{ px: 1, py: 0.75, borderRadius: 1.5, backgroundColor: colors.semantic.layer.accentLight }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            Note · {note.actorName}
          </Typography>
          <Typography variant="caption" sx={{ ml: 1, color: colors.semantic.text.secondary }}>
            {[note.sourceStep?.stepName, new Date(note.createdAt).toLocaleString()]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {note.content}
          </Typography>
          <NoteAttachmentList attachments={note.attachments} />
        </Box>
      ))}
    </Stack>
  );
};

/** Remove is an edit-placement control, and only while delete is open (§Late-docs: never once append-only). */
const deleteControlShown = (input: {
  isGlobalDocument: boolean;
  deleteDisabled: boolean;
  mode: string | undefined;
}): boolean => input.isGlobalDocument && !input.deleteDisabled && input.mode !== "view";

/**
 * When the Attach control renders. §Late-docs: an append-only document ALWAYS offers it — an occupied
 * single-file document takes a new version rather than a replacement, so occupancy is not a reason to hide
 * it. §History (product ask, 2026-08-06): archived files ("Previous unit" after Change Unit) are HISTORY,
 * not occupancy — a document whose every file is superseded still owes an upload control so the new unit's
 * paper can land beside the old one's.
 */
const attachControlShown = (input: {
  uploadDisabled: boolean;
  worksThroughRequests: boolean;
  appendOnly: boolean;
  typed: boolean;
  multiple: boolean;
  liveUploads: StepUpload[];
  hasGenerator: boolean;
}): boolean => {
  if (input.uploadDisabled || input.worksThroughRequests) return false;
  return (
    input.appendOnly ||
    input.typed ||
    input.multiple ||
    input.liveUploads.every((upload) => upload.decision === "REJECTED") ||
    input.liveUploads.length === 0 ||
    input.hasGenerator
  );
};

/** The Attach control's label — the one ask it makes of the agent right now. */
const attachControlLabel = (input: {
  typed: boolean;
  docType: string;
  multiple: boolean;
  appendOnly: boolean;
  occupied: boolean;
}): string => {
  if (input.typed && !input.docType) return "Choose a type to attach";
  if (input.multiple && input.occupied) return "Attach another file";
  if (input.appendOnly && input.occupied) return "Upload new version";
  return "Attach file";
};

const normalizePaymentMethod = (value: unknown): PaymentProofDetails["method"] | null => {
  if (typeof value !== "string") return null;
  const token = value.trim().toUpperCase().replace(/[^A-Z0-9]+/g, "_");
  return token === "INSTAPAY" || token === "BANK_TRANSFER" ? token : null;
};

/** Keep only the reference required by the selected method and trim values before upload/display. */
const paymentDetailsOf = (value: unknown): PaymentProofDetails | null => {
  if (value === null || typeof value !== "object") return null;
  const raw = value as {
    method?: unknown;
    mobileNumber?: unknown;
    transactionReferenceId?: unknown;
  };
  const method = normalizePaymentMethod(raw.method);
  if (method === "INSTAPAY") {
    const mobileNumber = typeof raw.mobileNumber === "string" ? raw.mobileNumber.trim() : "";
    return mobileNumber ? {method, mobileNumber} : null;
  }
  if (method === "BANK_TRANSFER") {
    const transactionReferenceId =
      typeof raw.transactionReferenceId === "string" ? raw.transactionReferenceId.trim() : "";
    return transactionReferenceId ? {method, transactionReferenceId} : null;
  }
  return null;
};

const paymentDetailsText = (details: PaymentProofDetails | null): string => {
  if (!details) return "Payment details not recorded";
  if (details.method === "INSTAPAY") return `Instapay · Sending mobile: ${details.mobileNumber}`;
  return `Bank transfer · Transaction reference: ${details.transactionReferenceId}`;
};

const uploadPaymentDetails = (
  paymentProof: boolean,
  mapped: boolean,
  answers: unknown,
  method: PaymentProofDetails["method"] | "",
  reference: string
): PaymentProofDetails | null => {
  if (!paymentProof) return null;
  if (mapped) return paymentDetailsOf(answers);
  const localAnswers = method === "INSTAPAY"
    ? {method, mobileNumber: reference}
    : {method, transactionReferenceId: reference};
  return paymentDetailsOf(localAnswers);
};

const hasProofDisplayContext = (
  resolvedContext: boolean | undefined,
  field: FormField,
  document: ResolvedDocument | null | undefined,
  uploads: StepUpload[],
  detailByUid: Map<string, DocumentUpload>
): boolean =>
  Boolean(resolvedContext || field.paymentProof || document?.paymentProof) ||
  uploads.some((upload) =>
    Boolean(paymentDetailsOf(detailByUid.get(upload.uid)?.payment ?? upload.payment))
  );

const PaymentDetails = ({
  payment,
  proofContext,
}: {
  payment: unknown;
  proofContext: boolean;
}) => {
  const colors = useTheme().nawy.colors;
  const details = paymentDetailsOf(payment);
  if (!details && !proofContext) return null;
  return (
    <Typography
      variant="caption"
      sx={{display: "block", mt: 0.5, color: colors.semantic.text.secondary}}
    >
      {paymentDetailsText(details)}
    </Typography>
  );
};

const uploadRowAppearance = (theme: Theme, needsChange: boolean, conditional: boolean) => {
  const colors = theme.nawy.colors;
  if (needsChange) return {
    border: `1px solid ${colors.semantic.border.extension.negative}`,
    borderLeft: `3px solid ${colors.semantic.border.extension.negative}`,
    backgroundColor: colors.semantic.layer.extension.negativeLight,
  };
  if (conditional) return {
    border: "1px solid #F76700",
    borderLeft: "3px solid #F76700",
    backgroundColor: "#FFF6F0",
  };
  return {border: `1px solid ${colors.semantic.border.primary}`};
};

const UploadFileIcon = ({upload}: {upload: StepUpload}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors;
  const isImage = /\.(png|jpe?g|gif|webp|heic|heif|bmp|svg)$/i.test(upload.fileName);
  let backgroundColor = colors.semantic.layer.accentLight;
  let color = colors.primitives.blue[500];
  if (/\.pdf$/i.test(upload.fileName)) {
    backgroundColor = colors.primitives.red[600];
    color = colors.primitives.white[100] ?? "#ffffff";
  } else if (isImage) {
    backgroundColor = colors.primitives.lime[700];
    color = colors.primitives.white[100] ?? "#ffffff";
  } else if (upload.decision === "REJECTED") {
    backgroundColor = colors.semantic.layer.extension.negativeLight;
    color = colors.primitives.red[500];
  }
  return (
    <Box sx={{backgroundColor, borderRadius: theme.nawy.radius.lg, p: 1, display: "flex"}}>
      {isImage ? <ImageIcon size={20} color={color} /> : <FileText size={20} color={color} />}
    </Box>
  );
};

const uploadDecisionColor = (theme: Theme, decision: FileDecision, conditional: boolean): string | undefined => {
  if (decision === "REJECTED") return theme.nawy.colors.semantic.text.extension.negative;
  if (conditional) return "#E65F00";
  return theme.nawy.colors.semantic.text.tertiary;
};

/**
 * The hidden input outlives the Attach block so Replace works on settled EDIT placements too — including a
 * card whose returned file still offers Replace. Returned means REJECTED or CONDITIONAL
 * (§Returned-resubmit) — the same set the Replace control admits; anything narrower leaves that control
 * clicking a ref that was never rendered. §Late-docs (live bite, 2026-09-07): a SETTLED card is `disabled`,
 * yet WM grants `capabilities.upload` for a late-addable document — the control rendered while the input did
 * not, so the click was a silent no-op. Same rule: whenever a control can call `inputRef.current?.click()`,
 * the input must exist.
 */
const fileInputMounted = (input: {
  disabled: boolean;
  uploadDisabled: boolean;
  viewOnly: boolean;
  uploads: StepUpload[];
  detailByUid: Map<string, DocumentUpload>;
}): boolean =>
  !input.viewOnly &&
  (!input.disabled ||
    !input.uploadDisabled ||
    input.uploads.some(
      (upload) =>
        (upload.decision === "REJECTED" || upload.decision === "CONDITIONAL") &&
        !input.detailByUid.get(upload.uid)?.superseded
    ));

const DocumentFieldHeader = ({
  field,
  document,
}: {
  field: FormField;
  document?: ResolvedDocument | null;
}) => {
  const colors = useTheme().nawy.colors;
  return (
    <>
      {/* §42 (QA): a field label, not a heading — small, regular, secondary */}
      <Stack direction="row" spacing={1} alignItems="center">
        {/* §MobileQA: Body/xs 11/16 — the same label metrics as FieldShell, which MUI's
            `caption` (12/20) disagreed with inside the same step body */}
        <Typography
          sx={{ fontSize: 11, lineHeight: "16px" }}
          color={colors.semantic.text.secondary}
        >
          {field.l}
        </Typography>
        {/* §Checklist-maintenance: the EFFECTIVE required flag from the read model */}
        {document?.required !== undefined && (
          <Box
            component="span"
            sx={{
              fontSize: "10px",
              fontWeight: 700,
              lineHeight: 1,
              px: 0.75,
              py: 0.4,
              borderRadius: "999px",
              color: document.required ? "#9a3412" : colors.semantic.text.secondary,
              backgroundColor: document.required ? "#ffedd5" : "#f3f4f6",
            }}
          >
            {document.required ? "Required" : "Optional"}
          </Box>
        )}
      </Stack>
      {field.help && (
        <Typography
          variant="body2"
          color={colors.semantic.text.secondary}
          sx={{ whiteSpace: "pre-line", mt: 0.5 }}
        >
          {field.help}
        </Typography>
      )}

      {/* §EOI: the recorded customer send of this document */}
      {document?.sentDocument?.sentAt && (
        <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "#166534" }}>
          Sent to customer{" "}
          {new Date(document.sentDocument.sentAt).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
          })}
          {document.sentDocument.sentBy ? ` by ${document.sentDocument.sentBy}` : ""}
        </Typography>
      )}
    </>
  );
};

const documentRequestControls = (
  document: ResolvedDocument | null | undefined,
  fileTypes: string[] | undefined,
  isGlobalDocument: boolean,
  disabled: boolean,
  viewOnly: boolean
): { requestable: boolean; worksThroughRequests: boolean } => {
  const hasCategories = (document?.docCategories?.length ?? 0) > 0;
  const hasFileTypes = (fileTypes?.length ?? 0) > 0;
  const requestable = !disabled && !viewOnly && isGlobalDocument && (hasCategories || hasFileTypes);
  return {
    requestable,
    worksThroughRequests:
      requestable &&
      !document?.generator &&
      (hasCategories || Boolean(document?.request)),
  };
};

const PaymentProofFields = ({visible, mappedPayment, paymentReady, paymentMethod, paymentReference,
  uploadingHere, setPaymentMethod, setPaymentReference}: {
  visible: boolean;
  mappedPayment: boolean;
  paymentReady: boolean;
  paymentMethod: PaymentProofDetails["method"] | "";
  paymentReference: string;
  uploadingHere: boolean;
  setPaymentMethod: (method: PaymentProofDetails["method"] | "") => void;
  setPaymentReference: (reference: string) => void;
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors;
  if (!visible) return null;
  if (mappedPayment) {
    if (paymentReady) return null;
    return (
      <Typography variant="caption" sx={{display: "block", mt: 1, color: colors.semantic.text.extension.negative}}>
        Complete the payment method and its reference before attaching a file.
      </Typography>
    );
  }
  return (
    <Box sx={{mt: 1.5}}>
      <Typography
        variant="caption"
        color={colors.semantic.text.secondary}
        sx={{display: "block", mb: 0.5}}
      >
        Payment method
      </Typography>
      <TextField
        select
        size="small"
        fullWidth
        value={paymentMethod}
        disabled={uploadingHere}
        onChange={(event) => {
          setPaymentMethod(event.target.value as PaymentProofDetails["method"]);
          setPaymentReference("");
        }}
        sx={fieldInputSx(theme)}
        slotProps={{
          select: {
            IconComponent: SelectChevron,
            displayEmpty: true,
            inputProps: {"aria-label": "Payment method"},
            renderValue: (selected) => {
              if (selected) return selected === "INSTAPAY" ? "Instapay" : "Bank transfer";
              return (
                <Box component="span" sx={{color: colors.semantic.text.tertiary}}>
                  Choose a payment method
                </Box>
              );
            },
          },
        }}
      >
        <MenuItem value="INSTAPAY">Instapay</MenuItem>
        <MenuItem value="BANK_TRANSFER">Bank transfer</MenuItem>
      </TextField>
      {paymentMethod && (
        <Box sx={{mt: 1}}>
          <Typography
            variant="caption"
            color={colors.semantic.text.secondary}
            sx={{display: "block", mb: 0.5}}
          >
            {paymentMethod === "INSTAPAY" ? "Sending mobile number" : "Transaction reference ID"}
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={paymentReference}
            disabled={uploadingHere}
            onChange={(event) => setPaymentReference(event.target.value)}
            sx={fieldInputSx(theme)}
            slotProps={{
              htmlInput: {
                "aria-label":
                  paymentMethod === "INSTAPAY"
                    ? "Sending mobile number"
                    : "Transaction reference ID",
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

const UploadDetails = ({upload, extra, superseded, conditional, amlEnabled, proofDisplayContext}: {
  upload: StepUpload;
  extra?: DocumentUpload;
  superseded: boolean;
  conditional: boolean;
  amlEnabled: boolean;
  proofDisplayContext: boolean;
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors;
  return (
    <Box sx={{ flex: "1 1 140px", minWidth: 0 }}>
      {/* §MobileQA: the design sets the filename at Body/md REGULAR — the semibold
          made every file row shout over its own document label */}
      <Typography
        noWrap
        sx={{
          fontSize: 14,
          lineHeight: "20px",
          color: colors.semantic.text.primary,
        }}
      >
        {displayFileName(upload.fileName)}
      </Typography>
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        alignItems="center"
        sx={{ flexWrap: "wrap" }}
      >
        {/* §MobileQA: the design's sub-line is Body/sm 12/18 in Text/Tertiary — MUI's
            caption resolves 12/20 and the tone was one step too dark */}
        <Typography
          sx={{
            fontSize: 12,
            lineHeight: "18px",
            ...(conditional ? { fontWeight: 600 } : {}),
          }}
          color={uploadDecisionColor(theme, upload.decision, conditional)}
        >
          {superseded ? "Previous unit" : decisionLabel(upload.decision)}
        </Typography>
        {/* §VerdictNote: the reviewer's words, IN FULL, for both verdicts that carry them —
            a condition and a rejection both ride `rejectionReason`.

            Two bugs were here. `noWrap` ellipsised the note to whatever fitted beside the
            decision label ("— KYC is missing loan purpos…"), which is precisely the half that
            matters — the condition IS the instruction. And the render was gated on
            `conditional`, so a REJECTED file showed the word "Rejected" and never the reason
            for it. `flexBasis: 100%` gives the note the next line of the wrapping row rather
            than letting it compete with the chips for the tail of the first. */}
        {(conditional || upload.decision === "REJECTED") && upload.rejectionReason && (
          <Typography
            variant="caption"
            sx={{
              flexBasis: "100%",
              color: conditional ? "#E65F00" : colors.semantic.text.extension.negative,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            — {upload.rejectionReason}
          </Typography>
        )}
        {/* §Typed uploads: which type this file was attached as */}
        {extra?.docType && (
          <Typography variant="caption" sx={{ color: colors.semantic.text.secondary }}>
            • {extra.docType}
          </Typography>
        )}
        {/* §Feedback-highlight: replaced a rejected file, awaiting re-review */}
        {extra?.resubmitted && upload.decision === "PENDING" && (
          <Typography variant="caption" sx={{ color: "#92400e", fontWeight: 600 }}>
            Resubmitted
          </Typography>
        )}
        {/* §Manual-AML: this file's screening outcome */}
        {amlEnabled && extra?.aml && (
          <Stack direction="row" spacing={0.4} alignItems="center">
            {/MATCH|HIT/.test((extra.aml.status ?? "").toUpperCase()) ? (
              <ShieldAlert size={12} color={amlTone(extra.aml).color} />
            ) : (
              <ShieldCheck size={12} color={amlTone(extra.aml).color} />
            )}
            <Typography
              variant="caption"
              sx={{ color: amlTone(extra.aml).color, fontWeight: 600 }}
            >
              {amlTone(extra.aml).label}
            </Typography>
          </Stack>
        )}
      </Stack>
      {extra?.resubmitted?.previousReason && (
        <Typography variant="caption" sx={{display: "block", mt: 0.5, color: colors.semantic.text.secondary}}>
          Previous decision: {extra.resubmitted.previousReason}
        </Typography>
      )}
      <NoteAttachmentList
        attachments={
          extra?.resubmitted?.previousAttachments ??
          extra?.decisionAttachments ??
          upload.decisionAttachments
        }
      />
      <PaymentDetails
        payment={extra ? extra.payment : upload.payment}
        proofContext={proofDisplayContext}
      />
    </Box>
  );
};

const UploadAmlControl = ({upload, extra, amlEnabled, disabled, viewOnly, fieldKey, mutations}: {
  upload: StepUpload;
  extra?: DocumentUpload;
  amlEnabled: boolean;
  disabled: boolean;
  viewOnly: boolean;
  fieldKey: string;
  mutations: ReturnType<typeof useStepMutations>;
}) => {
  const theme = useTheme();
  const showAmlRun =
    !disabled &&
    !viewOnly &&
    Boolean(amlEnabled) &&
    Boolean(extra?.amlActionable) &&
    (!extra?.aml || extra.aml.stale);
  const screening =
    mutations.amlScreen.isLoading &&
    mutations.amlScreen.variables?.uploadUid === upload.uid;
  if (!showAmlRun) return null;
  return (
    <Button
      size="small"
      variant="outlined"
      disabled={screening}
      startIcon={
        screening ? <CircularProgress size={13} /> : <ShieldCheck size={13} />
      }
      onClick={() =>
        mutations.amlScreen.mutate({ docKey: fieldKey, uploadUid: upload.uid })
      }
      sx={{ ...secondaryButtonSx(theme), mt: 1 }}
    >
      {screening ? "Screening…" : "Run AML check"}
    </Button>
  );
};

const DocumentField = ({
  field,
  fieldKey,
  caseUid,
  stepUid,
  uploads,
  disabled,
  mutations,
  fileTypes,
  document,
  proofDisplayContext: resolvedProofDisplayContext,
  paymentAnswers,
  flushDraft,
  lateAddition = false,
  notes,
}: {
  field: FormField;
  fieldKey: string;
  caseUid?: string | null;
  stepUid?: string;
  uploads: StepUpload[];
  disabled: boolean;
  mutations: ReturnType<typeof useStepMutations>;
  /** §TypedDocs: a multi-type placement's allowed types (registry `fileTypes`) — the engine REJECTS
   * an untyped upload into one ("requires a document type"), so the picker gates the Attach button. */
  fileTypes?: string[];
  /** §Documents: the placement's full resolved read model (config + per-file OCR/AML) when this
   * field is a document GLOBAL — absent for plain file fields, which have none of that. */
  document?: ResolvedDocument | null;
  /** Payment-proof identity used only for historical metadata display. Unlike upload validation,
   * an explicit placement false does not hide a proof identity resolved from the document registry. */
  proofDisplayContext?: boolean;
  /**
   * §paymentProof: the live answers this placement's payment questions currently hold. WM flags the
   * placement `paymentProof` and maps the three questions to fact keys (`paymentFields`); its upload
   * route REFUSES a file that arrives without them (422 PAYMENT_METHOD_INVALID / …). The CRM never
   * sent them, so an appraisal fee proof could not be uploaded from here at all — the agent just got
   * a validation error naming methods they had already chosen. Resolved by the step form, which is
   * what holds the live values.
   */
  paymentAnswers?: { method?: string; mobileNumber?: string; transactionReferenceId?: string };
  /** §Generated-forms: flush the step form's pending autosaves before rendering — the generated
   * document must describe what is on screen, not the last debounced draft (the applicant is
   * about to sign it). Same rule as WM's own generate handler. */
  flushDraft?: () => Promise<void>;
  /** §Late-docs-note: the step SETTLED — every attach offered here is a late addition, so a pick confirms
   * first (what it does + an optional note to credit) instead of dispatching at once. */
  lateAddition?: boolean;
  /** §Late-docs-note: the notes anchored to this document (`globals[].comments`) — read-only rows. */
  notes?: FieldNote[];
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors;
  const inputRef = useRef<HTMLInputElement>(null);
  // ref'd fields are document global placements → the /documents route (OCR + delete support)
  const isGlobalDocument = field.t === "document" || Boolean(field.ref);
  const viewOnly = field.mode === "view";
  const showInlineHistory = inlineHistoryEnabled(field, stepUid);
  const rejected = uploads.filter((upload) => upload.decision === "REJECTED");
  const effectiveFileTypes = fileTypes ?? document?.fileTypes;
  const typed = Boolean(effectiveFileTypes?.length);
  // §Multi-file placements: the registry (or the placement) allows several files — e.g. the bank
  // statements on Credit Documents (one file per month). The attach control must stay offered
  // after the first upload, or the CRM caps a multi-file document at one file.
  const multiple = Boolean(field.multiple);
  const paymentProof = Boolean(field.paymentProof ?? document?.paymentProof);
  // Mapping presence selects the legacy shared-field mode. Its current answers may still be incomplete;
  // answer-object truthiness cannot distinguish that state from a usable payment.
  const mappedPayment = paymentProof && field.paymentFields !== undefined;
  const [docType, setDocType] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<PaymentProofDetails["method"] | "">("");
  const [paymentReference, setPaymentReference] = useState("");
  // §Replace: attach flows through the same hidden input — a set target routes the next file
  // as a replacement of that exact upload instead of an addition.
  const [replaceTarget, setReplaceTarget] = useState<string | null>(null);
  // §Doc-request: a requested slot's Upload pins the composed docType for the next pick, so the
  // file lands against THAT request item without touching the picker.
  const [requestTypeTarget, setRequestTypeTarget] = useState<string | null>(null);
  // §Doc-request authoring: the "Request extra documents / Edit request" dialog (WM parity)
  const [requestOpen, setRequestOpen] = useState(false);
  // §Generated-forms: `mutations.generate` is a plain promise (not a react-query mutation), so the
  // button's busy state lives here — same shape as the KYC modal's `generating`.
  const [generating, setGenerating] = useState(false);
  // §Late-docs: the per-control axes WM serves for a late-addable document (see `lateDocsControls`).
  const { uploadDisabled, replaceDisabled, deleteDisabled, appendOnly } = lateDocsControls(
    document,
    isGlobalDocument,
    disabled,
    viewOnly
  );
  const { requestable, worksThroughRequests } = documentRequestControls(
    document,
    effectiveFileTypes,
    isGlobalDocument,
    disabled,
    viewOnly
  );

  // §Per-field busy: `mutations.upload` is ONE mutation shared by every document card on the step —
  // keying the spinner on the mutation's own variables stops an upload in one field from setting
  // every other Attach button spinning (live bite, 2026-08-05).
  const uploadingHere =
    mutations.upload.isLoading && mutations.upload.variables?.fieldKey === fieldKey;

  // extra per-file detail (OCR extraction, AML, type, superseded) by upload uid
  const detailByUid = new Map<string, DocumentUpload>(
    (document?.uploads ?? []).map((upload) => [upload.uid, upload])
  );
  // §History: superseded files render as history rows — they don't count as the document
  // being "filled" for the attach control below.
  const liveUploads = uploads.filter(
    (upload) => !detailByUid.get(upload.uid)?.superseded
  );
  const supersededUploads = uploads.filter((upload) =>
    Boolean(detailByUid.get(upload.uid)?.superseded)
  );
  const paymentForUpload = uploadPaymentDetails(
    paymentProof, mappedPayment, paymentAnswers, paymentMethod, paymentReference
  );
  const paymentReady = !paymentProof || paymentForUpload !== null;
  const proofDisplayContext = hasProofDisplayContext(
    resolvedProofDisplayContext, field, document, uploads, detailByUid
  );
  // §History: collapsed by default — the archived generations open on demand, like the WM
  const [historyOpen, setHistoryOpen] = useState(false);
  // §Late-docs-note: the files a settled-step pick is holding for the confirm modal; null ⇒ closed.
  const [pendingAddition, setPendingAddition] = useState<File[] | null>(null);

  const dispatchFiles = (
    files: File[],
    comment?: string,
    attachmentUids?: string[]
  ): Promise<unknown[]> => {
    const replacing = replaceTarget;
    const requestType = requestTypeTarget;
    if (paymentProof && !paymentForUpload) return Promise.resolve([]);
    // §Replace (live bite, 2026-08-05): a replacement INHERITS the replaced file's type — the
    // engine demands a docType on typed documents, and the picker state is empty on a bare
    // Replace click, so the call came back "requires a document type". The replaced upload
    // already knows what it is.
    const replacedType = replacing
      ? ((document?.uploads ?? []).find((upload) => upload.uid === replacing)?.docType ?? null)
      : null;
    const uploadFile = (file: File, index: number): Promise<unknown> => {
      // §Slot-multipick (live bite, 2026-09-02): a requested slot's pinned type applies to EVERY
      // picked file — the placement is `multiple`, so an agent picks all the bank statements at
      // once, and only the first used to carry the type: the rest went untyped and WM refused
      // them with the whole catalogue ("requires a document type — one of: …"). Only the
      // REPLACED file's type stays first-file-only, because only one file can be the replacement.
      const effectiveType =
        requestType ?? (index === 0 ? replacedType : null) ?? (docType || null);
      return mutations.upload.mutateAsync({
        fieldKey,
        file,
        isGlobalDocument,
        ...(typed && effectiveType ? { docType: effectiveType } : {}),
        // only the first picked file can be "the replacement" — the rest add on
        ...(replacing && index === 0 ? { replacesUploadUid: replacing } : {}),
        ...noteOnFirstFile(comment, index),
        ...(index === 0 && attachmentUids?.length ? {attachmentUids} : {}),
        // §paymentProof: send them only when the placement asks — an ordinary document must not
        // start carrying payment fields it never declared.
        ...(paymentForUpload ? { payment: paymentForUpload } : {}),
      });
    };
    const selectedFiles = paymentProof ? files.slice(0, 1) : files;
    const resetAfterSuccess = (result: unknown[]): unknown[] => {
      setReplaceTarget(null);
      setRequestTypeTarget(null);
      if (!mappedPayment) {
        setPaymentMethod("");
        setPaymentReference("");
      }
      return result;
    };
    if (attachmentUids?.length && selectedFiles.length > 1) {
      // Bind the note only after every companion file succeeds. A partial multi-pick failure then leaves
      // the staged note files unbound and retryable instead of committing them on the first response.
      return Promise.all(selectedFiles.slice(1).map((file, index) => uploadFile(file, index + 1)))
        .then(async (uploaded) => [await uploadFile(selectedFiles[0], 0), ...uploaded])
        .then(resetAfterSuccess);
    }
    return Promise.all(selectedFiles.map(uploadFile)).then(resetAfterSuccess);
  };

  const handleFiles = (files: FileList | null) => {
    if (viewOnly) return;
    if (!files?.length) return;
    if (!paymentReady) return;
    const picked = paymentProof ? [files[0]] : Array.from(files);
    if (inputRef.current) inputRef.current.value = "";
    // §Late-docs-note: on a settled step the pick confirms first — names the file(s), says what adding
    // does (reopens / extends the credit review) and offers an optional note to credit.
    if (lateAddition) {
      setPendingAddition(picked);
      return;
    }
    void dispatchFiles(picked).catch(() => undefined);
  };

  const cancelAddition = () => {
    setPendingAddition(null);
    // a cancelled pick must not leave the next plain pick routed as a replacement / requested slot
    setReplaceTarget(null);
    setRequestTypeTarget(null);
    if (!mappedPayment) {
      setPaymentMethod("");
      setPaymentReference("");
    }
  };

  /**
   * §Generated-forms (KYC parity, medical questionnaire &c.): render the placement's document from
   * the step's saved answers and download it. The server files the rendered copy on this card as a
   * SYSTEM upload unless a human upload — the signed scan — is already the newest file there.
   * Answers are fresh by the time anyone clicks: the step form autosaves every edit.
   */
  const generateForm = async () => {
    if (!document?.generator || generating) return;
    setGenerating(true);
    try {
      await flushDraft?.();
      const fileName = await mutations.generate(fieldKey, `${field.l || "Document"}.pdf`);
      if (fileName) toast.success(`${fileName} downloaded.`);
    } finally {
      setGenerating(false);
    }
  };

  const startReplace = (uploadUid: string) => {
    setReplaceTarget(uploadUid);
    inputRef.current?.click();
  };

  // §MobileQA: the design's trailing file action is a 32×32 Layer/Primary square with a 1px
  // Border/Primary edge at radius lg around a 16px glyph. MUI's `size="small"` drew ~26px, and
  // the bordered ones sat at radius md while Download carried no edge at all — so the cluster
  // read as three different controls.
  const fileActionSx = {
    width: 32,
    height: 32,
    flexShrink: 0,
    borderRadius: theme.nawy.radius.lg,
    border: `1px solid ${colors.semantic.border.primary}`,
    backgroundColor: colors.semantic.layer.primary,
    color: colors.semantic.text.secondary,
  };

  /** §Doc-request: one requested item as a slot row — shared by the step's OWN request and the
   * inbound cross-step asks (a review send-back), which upload through the same pinned-docType path. */
  const renderRequestedItem = (item: DocumentRequestItem) => {
    const uploaded = item.status === "uploaded";
    return (
      <Stack
        key={item.docType}
        direction="row"
        spacing={1.5}
        alignItems="center"
        useFlexGap
        sx={{
          // §Mobile: name + "Required" pill + Upload exceed 390px as one nowrap row
          flexWrap: "wrap",
          rowGap: 1,
          border: `1px solid ${colors.semantic.border.secondary}`,
          borderRadius: theme.nawy.radius.lg,
          p: 1.25,
        }}
      >
        <FileText size={16} color={colors.semantic.text.secondary} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {item.type}
          </Typography>
          {item.category && (
            <Typography variant="caption" color={colors.semantic.text.secondary}>
              {item.category}
            </Typography>
          )}
          {uploaded && (
            <Typography variant="caption" sx={{ display: "block", color: "#166534" }} noWrap>
              {(item.files ?? []).map((file) => file.fileName).join(", ") || item.fileName}
            </Typography>
          )}
        </Box>
        <Box
          component="span"
          sx={{
            fontSize: "11px",
            fontWeight: 700,
            lineHeight: 1,
            px: 0.75,
            py: 0.4,
            borderRadius: "999px",
            whiteSpace: "nowrap",
            color: item.required !== false ? "#9a3412" : colors.semantic.text.secondary,
            backgroundColor: item.required !== false ? "#ffedd5" : "#f3f4f6",
          }}
        >
          {item.required !== false ? "Required" : "Optional"}
        </Box>
        {uploaded ? (
          <Check size={16} color="#166534" style={{ flexShrink: 0 }} />
        ) : (
          !disabled && !viewOnly && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<Upload size={13} />}
              disabled={uploadingHere || !paymentReady}
              onClick={() => {
                setRequestTypeTarget(item.docType);
                inputRef.current?.click();
              }}
              sx={secondaryButtonSx(theme)}
            >
              Upload
            </Button>
          )
        )}
      </Stack>
    );
  };

  const renderUploadRow = (upload: (typeof uploads)[number]) => {
          const extra = detailByUid.get(upload.uid);
          const superseded = Boolean(extra?.superseded);
          // §Rejected-highlight (live bite, 2026-08-05): the file the agent must CHANGE gets the
          // loudest treatment on the card — tinted row + red rail + a labeled Replace button —
          // the Required chip alone did not pull the eye to the actual work item.
          const needsChange = upload.decision === "REJECTED" && !superseded;
          // §Conditional: approved WITH conditions — amber treatment + the reviewer's note
          const conditional = upload.decision === "CONDITIONAL" && !superseded;
          // §Under-review: the reviewer who returned this file has not submitted — WM refuses the
          // settled-step swap until they do (prod case 2522), so the row says so instead of offering Replace.
          const underReview = (needsChange || conditional) && Boolean(extra?.underReview);
          return (
            <Box
              key={upload.uid}
              sx={{
                ...uploadRowAppearance(theme, needsChange, conditional),
                borderRadius: theme.nawy.radius.lg,
                // §MobileQA: the design's File List is Border/Primary at 12px padding — the row
                // was drawn on the lighter Border/Secondary with 10px, so it read as a hairline
                p: 1.5,
                opacity: superseded ? 0.55 : 1,
              }}
            >
              {/* §Mobile: when the filename would shrink below readability, let the action
                  buttons wrap to their own line instead of eating the name */}
              <Stack
                direction="row"
                spacing={1.5}
                useFlexGap
                alignItems="center"
                sx={{ flexWrap: "wrap", rowGap: 0.75 }}
              >
                <UploadFileIcon upload={upload} />
                <UploadDetails upload={upload} extra={extra} superseded={superseded}
                  conditional={conditional} amlEnabled={Boolean(document?.amlEnabled)}
                  proofDisplayContext={proofDisplayContext} />
                {/* §Download: a document the agent cannot replace here is one the system PRODUCED
                    for them (the generated appraisal-fee form, the EOI, a contract) — those get a
                    real "Download" button, because collecting the file IS the task. */}
                {disabled ? (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<Download size={14} />}
                    disabled={mutations.downloading?.includes(upload.uid)}
                    onClick={() => mutations.download(upload.uid, displayFileName(upload.fileName))}
                    sx={primaryButtonSx(theme)}
                  >
                    {mutations.downloading?.includes(upload.uid) ? "Preparing…" : "Download"}
                  </Button>
                ) : (
                  <IconButton
                    size="small"
                    disabled={mutations.downloading?.includes(upload.uid)}
                    onClick={() => mutations.download(upload.uid, upload.fileName)}
                    aria-label="Download file"
                    sx={fileActionSx}
                  >
                    <Download size={16} />
                  </IconButton>
                )}
                {/* §Replace: supersede THIS file with a corrected scan (keeps multi-file siblings).
                    A REJECTED file's replace is THE pending action — labeled and red, not an icon —
                    and it stays offered on a settled EDIT placement (§Rejected-resubmit: the engine
                    waives the settled gate for exactly this write).
                    §Returned-resubmit: CONDITIONAL is a returned decision too. The engine's
                    `isReturnedDecision` is `REJECTED || CONDITIONAL`, and its settled-placement
                    fallback admits the swap of either — gating the control on `needsChange` alone
                    stranded an approved-with-conditions file on a settled step with no way to
                    supersede it, which is the case the engine went out of its way to allow. */}
                {renderWhen(
                  replaceControlShown({
                    isGlobalDocument,
                    replaceDisabled,
                    viewOnly,
                    needsChange,
                    conditional,
                    underReview,
                    canCorrectReplacement: !disabled && extra?.canCorrectReplacement,
                    superseded,
                  }),
                  (
                  needsChange ? (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={uploadingHere || !paymentReady}
                      startIcon={
                        uploadingHere ? <CircularProgress size={13} /> : <RefreshCw size={13} />
                      }
                      onClick={() => startReplace(upload.uid)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                        color: colors.semantic.text.extension.negative,
                        borderColor: colors.semantic.border.extension.negative,
                        borderRadius: theme.nawy.radius.lg,
                        "&:hover": {
                          borderColor: colors.semantic.border.extension.negative,
                          backgroundColor: "#F9DEDC",
                        },
                      }}
                    >
                      Replace
                    </Button>
                  ) : (
                    <IconButton
                      size="small"
                      disabled={uploadingHere || !paymentReady}
                      onClick={() => startReplace(upload.uid)}
                      aria-label="Replace file"
                      sx={fileActionSx}
                    >
                      <RefreshCw size={16} />
                    </IconButton>
                  )
                  )
                )}
                {renderWhen(
                  underReview && replaceDisabled && !viewOnly && isGlobalDocument,
                  (
                    <Typography
                      variant="caption"
                      title={`"${extra?.underReview?.stepName ?? "The review"}" has not submitted its decision yet. The file can be replaced once that review is submitted.`}
                      sx={{
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                        fontWeight: 600,
                        color: theme.palette.warning.dark,
                      }}
                    >
                      Under review — {extra?.underReview?.stepName}
                    </Typography>
                  )
                )}
                {/* §Delete: offered exactly where the engine allows one —
                    `deleteDocumentUpload` (workflow-file.service.ts) requires an EDITABLE placement
                    for the doc key, and normalizes the mode as "anything but view is edit"
                    (form-schema.helper.ts:564). A generatable slot is NOT an exception: the KYC
                    capture step authors {ref:'kyc-document', mode:'edit'}, so its filed copy — the
                    unsigned rendered form included — is deletable, and an agent who attached the
                    wrong file needs that. A `view` placement (Credit Review) offers nothing, which
                    is also what the engine would answer. */}
                {renderWhen(deleteControlShown({ isGlobalDocument, deleteDisabled, mode: field.mode }), (
                  <IconButton
                    size="small"
                    disabled={mutations.removeUpload.isLoading}
                    onClick={() =>
                      mutations.removeUpload.mutate({
                        docKey: fieldKey,
                        uploadUid: upload.uid,
                      })
                    }
                    aria-label="Remove file"
                    sx={fileActionSx}
                  >
                    <Trash2 size={16} />
                  </IconButton>
                ))}
              </Stack>
              <UploadAmlControl upload={upload} extra={extra} amlEnabled={Boolean(document?.amlEnabled)}
                disabled={disabled} viewOnly={viewOnly} fieldKey={fieldKey} mutations={mutations} />
              {/* §Review-appeal: the challenge to THIS file's decision, under the reviewer's reason for
                  it. Per row, not per card: a multi-file placement, a per-participant document and the
                  two faces of a pair all share one placement key, and each file carries its own
                  decision and its own decider — so a card-level button could hand the appeal to the
                  wrong reviewer. Renders nothing on a file with no standing rejection and no thread. */}
              <InlineAppeal
                anchor={{ kind: "document", fieldKey, uploadUid: upload.uid }}
                label={field.l}
              />
            </Box>
          );
  };


  return (
    // §MobileQA: no card of its own. StepCard already draws the design's white Slot (radius xl,
    // 16px) around the form; the extra bordered white box made it white-on-white and cost ~34px
    // of inner width on a 390px screen. A document is a bare label + file-list group in that slot.
    <Box>
      <DocumentFieldHeader field={field} document={document} />

      <PaymentProofFields
        visible={paymentProof && fileInputMounted({disabled, uploadDisabled, viewOnly, uploads, detailByUid})}
        mappedPayment={mappedPayment}
        paymentReady={paymentReady}
        paymentMethod={paymentMethod}
        paymentReference={paymentReference}
        uploadingHere={uploadingHere}
        setPaymentMethod={setPaymentMethod}
        setPaymentReference={setPaymentReference}
      />

      {rejected.length > 0 && (
        <Box
          sx={{
            backgroundColor: colors.semantic.layer.extension.negativeLight,
            borderRadius: theme.nawy.radius.lg,
            p: 1.5,
            my: 1,
          }}
        >
          <Typography
            variant="caption"
            color={colors.semantic.text.extension.negative}
            fontWeight={600}
          >
            Below documents required
          </Typography>
          {rejected.map((upload) => (
            <Box key={upload.uid} sx={{mt: 0.5}}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CircleAlert size={16} color={colors.primitives.red[500]} />
                <Typography variant="body2">
                  {displayFileName(upload.fileName)}
                  {upload.rejectionReason ? ` — ${upload.rejectionReason}` : ""}
                </Typography>
              </Stack>
              <NoteAttachmentList attachments={upload.decisionAttachments} />
            </Box>
          ))}
        </Box>
      )}

      {/* §Two-sided: one card per OWNER (front/back pairs) — replaces the flat upload list, the
          document-level extraction block AND the attach control below. A two-sided document has
          no document-level values, and its uploads are slot-addressed, never free-attached. */}
      {document?.twoSided ? (
        <TwoSidedDocument
          fieldKey={fieldKey}
          document={document}
          disabled={disabled || viewOnly}
          mutations={mutations}
        />
      ) : (
        <>
      {/* §Doc-request: the documents an agent asked for here — one slot per requested item
          (WM parity). FROM_CLIENT means the customer sees these in their app and was notified;
          a slot's Upload pins that item's composed docType so the file files itself. */}
      {document?.request && (
        <Box sx={{ mt: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                px: 1,
                py: 0.4,
                borderRadius: "999px",
                border: `1px solid ${colors.semantic.border.secondary}`,
                backgroundColor: colors.semantic.layer.primary,
              }}
            >
              <Send size={12} color={colors.semantic.text.secondary} />
              <Typography variant="caption" color={colors.semantic.text.secondary}>
                {document.request.mode === "FROM_CLIENT"
                  ? `Requested from client · ${document.request.requestedBy}`
                  : `Requested (on behalf) · ${document.request.requestedBy}`}
              </Typography>
            </Box>
            {requestable && (
              // §MobileQA: a bare MUI text button painted itself MUI-default #1976d2 — the theme
              // declares no palette. §BlueButtons: it belongs on the module's tonal ramp.
              <Button
                size="small"
                onClick={() => setRequestOpen(true)}
                sx={{ ...secondaryButtonSx(theme), textTransform: "none" }}
              >
                Edit request
              </Button>
            )}
          </Stack>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {document.request.items.map(renderRequestedItem)}
          </Stack>
          {document.request.comment && (
            <Typography variant="caption" sx={{display: "block", mt: 0.5, whiteSpace: "pre-wrap"}}>
              Note: {document.request.comment}
            </Typography>
          )}
          <NoteAttachmentList attachments={document.request.attachments} />
        </Box>
      )}

      {/* §Cross-step-request: ARMED asks raised on OTHER steps (a review send-back) — fulfil-only
          slots. The origin step owns the ask, so there is no Edit request affordance here. */}
      {(document?.inboundRequests ?? []).map((ask, index) => (
        <Box key={`${ask.requestedAt}-${index}`} sx={{ mt: 1 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.4,
              borderRadius: "999px",
              border: "1px solid #fdba74",
              backgroundColor: "#ffedd5",
            }}
          >
            <CircleAlert size={12} color="#9a3412" />
            <Typography variant="caption" sx={{ color: "#9a3412" }}>
              Requested by {ask.originStepName ?? "a review step"} · {ask.requestedBy}
            </Typography>
          </Box>
          {ask.comment && (
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 0.5, whiteSpace: "pre-wrap" }}
            >
              Note: {ask.comment}
            </Typography>
          )}
          <NoteAttachmentList attachments={ask.attachments} />
          <Stack spacing={1} sx={{ mt: 1 }}>
            {ask.items.map(renderRequestedItem)}
          </Stack>
        </Box>
      ))}

      <Stack spacing={1} sx={{ mt: 1 }}>
        {/* §History (product ask, 2026-08-06): archived files live behind an expandable
            "History" list like the WM — live files always show, superseded expand BELOW the toggle */}
        {liveUploads.map(renderUploadRow)}
        {/* §History: the toggle — collapsed by default, count says what's inside */}
        {!showInlineHistory && supersededUploads.length > 0 && (
          <Button
            size="small"
            variant="text"
            startIcon={<HistoryIcon size={13} />}
            endIcon={
              historyOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />
            }
            onClick={() => setHistoryOpen((open) => !open)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12,
              alignSelf: "flex-start",
              color: colors.semantic.text.secondary,
            }}
          >
            History ({supersededUploads.length})
          </Button>
        )}
        {!showInlineHistory && historyOpen && supersededUploads.map(renderUploadRow)}
      </Stack>

      {/* §OCR review: the canonical extracted values (review/confirm/edit live here) */}
      {isGlobalDocument && uploads.length > 0 && (
        <DocumentExtraction
          docKey={fieldKey}
          fields={document?.ocrFields ?? []}
          confirmed={Boolean(document?.confirmed)}
          editable={!disabled && !viewOnly}
          mutations={mutations}
        />
      )}

      <DocumentNotes notes={notes} />

      {renderWhen(
        attachControlShown({
          uploadDisabled,
          worksThroughRequests,
          appendOnly,
          typed,
          multiple,
          liveUploads,
          hasGenerator: Boolean(document?.generator),
        }),
        (
          <>
            {/* §TypedDocs: the engine requires a type per file on a multi-type placement — pick
                first. Standard module select (fieldInputSx + chevron + BOUNDED menu): the default
                floating-label variant grew an unstyled full-height menu with 20 long options. */}
            {typed && (
              <Box sx={{ mt: 1.5 }}>
                <Typography
                  variant="caption"
                  color={colors.semantic.text.secondary}
                  sx={{ display: "block", mb: 0.5 }}
                >
                  Document type
                </Typography>
                <TextField
                  select
                  size="small"
                  fullWidth
                  value={docType}
                  onChange={(event) => setDocType(event.target.value)}
                  sx={fieldInputSx(theme)}
                  slotProps={{
                    select: {
                      IconComponent: SelectChevron,
                      displayEmpty: true,
                      renderValue: (selected) =>
                        (selected as string) || (
                          <Box component="span" sx={{ color: colors.semantic.text.tertiary }}>
                            Choose a type…
                          </Box>
                        ),
                      MenuProps: { PaperProps: { sx: { maxHeight: 320 } } },
                    },
                    htmlInput: { "aria-label": "Document type" },
                  }}
                >
                  {(effectiveFileTypes ?? []).map((type) => (
                    <MenuItem key={type} value={type} sx={{ fontSize: 14 }}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            )}
            {/* §Generated-forms: the placement advertises an on-demand generator (KYC form, medical
                questionnaire) — offer the filled official form beside the Attach control, so the
                download → print → sign → upload-the-scan loop lives on the card itself. */}
            {Boolean(document?.generator) && (
              <Button
                variant="outlined"
                size="small"
                startIcon={
                  generating ? <CircularProgress size={14} /> : <Download size={14} />
                }
                disabled={generating}
                onClick={generateForm}
                sx={{ ...secondaryButtonSx(theme), mt: 1.5, mr: 1 }}
              >
                {document?.generator?.label ?? "Download filled form"}
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={
                uploadingHere ? <CircularProgress size={14} /> : <Paperclip size={14} />
              }
              disabled={uploadingHere || !paymentReady || (typed && !docType)}
              onClick={() => inputRef.current?.click()}
              sx={{ ...secondaryButtonSx(theme), mt: 1.5 }}
            >
              {attachControlLabel({
                typed,
                docType,
                multiple,
                appendOnly,
                occupied: liveUploads.length > 0,
              })}
            </Button>
            {/* §Late-docs: say what adding a file here DOES — it is not a quiet correction. */}
            {renderWhen(
              appendOnly,
              <Typography
                variant="caption"
                sx={{ display: "block", mt: 0.75, color: colors.semantic.text.secondary }}
              >
                Append-only — adding a file reopens or extends the credit review; existing files stay as
                history.
              </Typography>
            )}
          </>
        )
      )}
      {/* §Doc-request authoring: raise a request when none exists ("Edit request" lives on the
          chip row above once one does). Labelled for what the tap DOES — the dialog it opens is a
          type picker — rather than WM's "Request extra documents"; the dialog keeps that title. */}
      {requestable && !document?.request && (
        // §MobileQA: a bare MUI text button — no brand colour, no design metrics
        <Button
          variant="contained"
          disableElevation
          startIcon={<Send size={14} />}
          onClick={() => setRequestOpen(true)}
          sx={{ ...primaryButtonSx(theme), textTransform: "none", mt: 1, alignSelf: "flex-start" }}
        >
          Choose document type
        </Button>
      )}
      {requestable && (
        <DocRequestDialog
          open={requestOpen}
          onClose={() => setRequestOpen(false)}
          docKey={fieldKey}
          caseUid={caseUid}
          stepUid={stepUid}
          document={document}
          fileTypes={effectiveFileTypes}
          mutations={mutations}
        />
      )}
      {/* §Late-docs-note: a settled-step pick confirms here — the file(s), what adding does, an optional note. */}
      <ActionConfirmModal
        request={lateAdditionRequest(pendingAddition, field.l)}
        busy={uploadingHere}
        caseUid={caseUid}
        attachmentContext={
          caseUid && stepUid && pendingAddition
            ? {kind: "document-upload", stepUid, fieldKey}
            : null
        }
        onCancel={cancelAddition}
        onConfirm={(comment, attachmentUids) => {
          const files = pendingAddition ?? [];
          return dispatchFiles(files, comment, attachmentUids);
        }}
      />
      {/* the hidden input outlives the Attach block — see `fileInputMounted` for the rule. */}
      {renderWhen(
        fileInputMounted({ disabled, uploadDisabled, viewOnly, uploads, detailByUid }),
        <input
          ref={inputRef}
          type="file"
          hidden
          multiple={multiple && !replaceTarget && !paymentProof}
          onChange={(event) => handleFiles(event.target.files)}
        />
      )}
        </>
      )}
      <InlineDocumentHistory field={field} fieldKey={fieldKey} stepUid={stepUid} enabled={showInlineHistory} />
    </Box>
  );
};

export default DocumentField;
